import React from 'react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/Button';
import { Undo2 } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function generateErrorId(): string {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.random().toString(16).slice(2, 6).toUpperCase();
  return `ERR-${date}-${rand}`;
}

function getErrorCode(error: Error | null): string {
  if (!error) return '500 · Neznámá chyba';
  const msg = error.message?.toLowerCase() ?? '';
  if (msg.includes('chunk') || msg.includes('loading')) return '460 · Chyba načítání';
  if (msg.includes('network') || msg.includes('fetch')) return '503 · Síťová chyba';
  if (msg.includes('timeout')) return '504 · Časový limit';
  if (msg.includes('permission') || msg.includes('forbidden')) return '403 · Přístup zamítnut';
  if (msg.includes('not found')) return '404 · Nenalezeno';
  return '500 · Chyba aplikace';
}

const ErrorGlyph = () => (
  <svg width="96" height="96" viewBox="0 0 84 84" className="shrink-0">
    <circle cx="42" cy="42" r="38" fill="white" stroke="var(--primary-100)" strokeWidth="2" />
    <circle
      cx="42"
      cy="42"
      r="30"
      fill="none"
      stroke="var(--primary-200)"
      strokeWidth="1"
      strokeDasharray="3 4"
    />
    <path
      d="M42 22 L62 56 L22 56 Z"
      fill="var(--primary-50)"
      stroke="var(--primary-700)"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <rect x="40" y="32" width="4" height="14" fill="var(--primary-700)" rx="1" />
    <circle cx="42" cy="51" r="2" fill="var(--primary-700)" />
  </svg>
);

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private errorId: string;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.errorId = generateErrorId();
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    toast.error('Nastala chyba', { description: error.message });
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    // Log to error reporting service if needed
  }

  render() {
    if (this.state.hasError) {
      const errorCode = getErrorCode(this.state.error);

      return (
        <div className="min-h-screen bg-neutral-50 relative overflow-hidden flex flex-col">
          {/* Decorative grid background */}
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, var(--neutral-200) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 50% 30%, rgba(255,255,255,0) 0%, rgba(246,248,250,0.9) 70%)',
            }}
          />

          {/* Minimal top bar */}
          <header className="relative z-10 h-14 flex items-center px-8 border-b border-neutral-150 bg-white/70 backdrop-blur-sm">
            <span className="text-sm font-semibold tracking-tight text-neutral-800">MOIRY</span>
            <div className="ml-auto">
              <Badge variant="outline" size="md" className="font-mono">
                {this.errorId}
              </Badge>
            </div>
          </header>

          <div className="relative z-10 flex-1 flex items-center justify-center px-8 py-12">
            <div className="w-full max-w-xl text-center">
              <div className="flex justify-center mb-7">
                <ErrorGlyph />
              </div>

              <Badge variant="danger" size="md" className="mb-4">
                {errorCode}
              </Badge>

              <h1 className="text-4xl font-semibold tracking-tight text-neutral-900 leading-[1.05]">
                Něco se pokazilo.
              </h1>

              <p className="text-base text-neutral-600 max-w-md mx-auto leading-relaxed mt-4">
                Aplikace narazila na neočekávanou chybu. Vaše práce byla automaticky uložena — o nic
                nepřijdete.
              </p>

              <div className="mt-8">
                <Button
                  data-testid="error-boundary-reload-btn"
                  size="lg"
                  onClick={() => window.location.reload()}
                >
                  <Undo2 size={14} /> Načíst znovu
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
